import React, { useState } from 'react';
import { getUserSlice } from '../../context/store/store';
import { AlertModal } from '../../shared/Modal/AlertModal';
import { constants } from '../../context/constants';
import { useNavigate } from 'react-router-dom';
import { currencyValue } from '../../helpers/currencyHelper';
import { clearAuthenticatedUser } from '../../context/store/userSynchronization';
import './UserNav.css';

// The dropdown items are plain divs (not buttons/links), so keyboard access
// needs the same Enter/Space activation a native control would give for free.
const activateOnKey = (event, action) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  action();
};

const UserNav = ({ letters, fixedMobile }) => {
  const navigator = useNavigate();
  const [hover, setHover] = useState(false);
  const [openByClick, setOpenByClick] = useState(false);
  // Mouse hover alone never fires for keyboard navigation — without this,
  // Tab-focusing into the dropdown's own items leaves them focused but
  // still visually `visually-hidden`. React's onFocus/onBlur are focusin/
  // focusout under the hood (bubble from descendants); relatedTarget tells
  // us whether focus actually left the whole container, as opposed to
  // moving between two of its own items.
  const [focusWithin, setFocusWithin] = useState(false);
  const showDropdown = !fixedMobile && (hover || focusWithin || openByClick);
  const { userName, headers, getWallet, role } = getUserSlice();
  const [alertModalShow, setAlertModalShow] = useState(false);
  const [messagesToModal, setMessagesToModal] = useState({ title: '', body: '' });
  const { getMobileDevice } = getUserSlice();
  const isMobileDevice = getMobileDevice();

  const goTo = (path) => {
    setOpenByClick(false);
    navigator(path);
  };

  const toMyAccount = () => goTo('/account');
  const toMyMovements = () => goTo('/movements');
  const toRecharge = () => goTo('/recharge');
  const toRechargeHistory = () => goTo('/recharge/historial');
  const toCredentialTemplate = () => goTo('/distribuidor/plantilla');

  const onCloseModal = () => {
    setAlertModalShow(false);
    navigator('/');
  }

  const signOut = () => {
    setOpenByClick(false);
    clearAuthenticatedUser();
    setAlertModalShow(true);
    setMessagesToModal({ title: constants.MODAL_TITLE_SIGNOUT, body: constants.MODAL_BODY_SIGNOUT });
  }

  const filterAndCursorStyle = (hover && !isMobileDevice) ? {
    filter: "invert(0.4) sepia(1) hue-rotate(20deg) saturate(100%)",
    cursor: 'pointer'
  } : undefined;

  const IconBox = () => {
    return (
      <div className={'d-flex align-items-center'} style={filterAndCursorStyle}>
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="currentColor" className="bi bi-person" viewBox="0 0 16 16">
          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z" />
        </svg>
      </div>
    );
  }

  return (
    <div
      className="position-relative userNavRoot"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocusWithin(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocusWithin(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setOpenByClick(false);
          setFocusWithin(false);
        }
      }}
    >
      {!Object.keys(headers).length ?
        <div className='d-flex'>
          <IconBox />
          {letters &&
            <div className='d-flex flex-column align-items-start ms-2'>
              <span className='noDisplay'>Inicia sesión</span>
              <span className='noDisplay'>a tu cuenta</span>
            </div>
          }
        </div>
        :
        <div className='d-flex'>
          {fixedMobile
            ? <>
              <div className='d-flex flex-column align-items-center ms-2' style={filterAndCursorStyle}>
                <IconBox />
                <span>{currencyValue(getWallet())}</span>
              </div>
            </>
            : <button
                type='button'
                className='userNavTrigger d-flex align-items-center'
                aria-haspopup='menu'
                aria-expanded={showDropdown}
                onClick={() => setOpenByClick((isOpen) => !isOpen)}
              >
                <IconBox />
                <span className='d-flex flex-column align-items-start ms-2' style={filterAndCursorStyle}>
                  <span>{userName.split(' ')[0]}</span>
                  <span>{currencyValue(getWallet())}</span>
                </span>
              </button>
          }
          {!fixedMobile && (
            <div className={`userDropdownLayer position-absolute top-100 ${!showDropdown && 'visually-hidden'}`}>
              <div className={!isMobileDevice ? 'dropStyle' : ''} role='menu'>
                <div className='divOption' role='menuitem' tabIndex={0} onClick={toMyAccount} onKeyDown={(event) => activateOnKey(event, toMyAccount)}>Mi cuenta</div>
                <div className='divOption' role='menuitem' tabIndex={0} onClick={toMyMovements} onKeyDown={(event) => activateOnKey(event, toMyMovements)}>Pedidos</div>
                <div className='divOption' role='menuitem' tabIndex={0} onClick={toRecharge} onKeyDown={(event) => activateOnKey(event, toRecharge)}>Recargar saldo</div>
                <div className='divOption' role='menuitem' tabIndex={0} onClick={toRechargeHistory} onKeyDown={(event) => activateOnKey(event, toRechargeHistory)}>Historial de recargas</div>
                {constants.CREDENTIAL_TEMPLATE_ROLES.includes(role) && (
                  <div className='divOption' role='menuitem' tabIndex={0} onClick={toCredentialTemplate} onKeyDown={(event) => activateOnKey(event, toCredentialTemplate)}>Personalizar plantilla</div>
                )}
                <div className='divOption' role='menuitem' tabIndex={0} onClick={signOut} onKeyDown={(event) => activateOnKey(event, signOut)}>Cerrar sesión</div>
              </div>
            </div>
          )}
        </div>
      }
      <AlertModal
        show={alertModalShow}
        onHide={() => onCloseModal()}
        title={messagesToModal.title}
        bodyText={messagesToModal.body}
      />
    </div>
  );
};

export { UserNav };
